import { useCallback, useMemo } from 'react'
import { enqueueSnackbar } from 'notistack'

import { ddeApi } from '@/service/ddeApi'
import { StandarizedGolfPlayer } from '@/views/DataAdmin/types'

import { useActionMenuContext } from '../context/ActionMenuContext'
import { MenuItemLike } from '../index.types'
import { ActionMergeQuarantineConfirm } from './ActionMergeQuarantineConfirm'

const makeMergeConfirmationMessage = (
  quarantinePlayer: StandarizedGolfPlayer,
  similarPlayer: StandarizedGolfPlayer
) =>
  `You're about to merge player ${quarantinePlayer.displayName} (${quarantinePlayer.id}) to ${similarPlayer.displayName} (${similarPlayer.id}).` as const

const makeMergeSuccessMessage = (
  quarantinePlayer: StandarizedGolfPlayer,
  similarPlayer: StandarizedGolfPlayer
) =>
  `Successfuly merged player ${quarantinePlayer.displayName} (${quarantinePlayer.id}) to ${similarPlayer.displayName} (${similarPlayer.id}).` as const

const errorMsg = `Merge unsuccessful.` as const

type Props = MenuItemLike & {
  onSubmit?: () => void
  quarantinePlayer: StandarizedGolfPlayer
  setLoading?: (isLoading: boolean) => void
}

export const ActionMergeQuarantine = ({
  quarantinePlayer,
  overrideText,
  displaySeparately,
  onSubmit,
  setLoading
}: Props) => {
  const golfEndpoints = ddeApi.endpoints.golf

  const { row: similarPlayer } = useActionMenuContext<StandarizedGolfPlayer>()

  const mergeResultPlayer: StandarizedGolfPlayer = useMemo(
    () => ({
      id: similarPlayer.id ?? quarantinePlayer.id,
      etId: quarantinePlayer.etId ?? similarPlayer.etId,
      etRanking: quarantinePlayer.etRanking ?? similarPlayer.etRanking,
      pgaId: quarantinePlayer.pgaId ?? similarPlayer.pgaId,
      lpgaId: quarantinePlayer.lpgaId ?? similarPlayer.lpgaId,
      livId: quarantinePlayer.livId ?? similarPlayer.livId,
      randaId: quarantinePlayer.randaId ?? similarPlayer.randaId,
      displayName: quarantinePlayer.displayName ?? similarPlayer.displayName,
      firstName: quarantinePlayer.firstName ?? similarPlayer.firstName,
      lastName: quarantinePlayer.lastName ?? similarPlayer.lastName,
      country: quarantinePlayer.country ?? similarPlayer.country,
      dateOfBirth: quarantinePlayer.dateOfBirth ?? similarPlayer.dateOfBirth,
      gender: similarPlayer.gender ?? quarantinePlayer.gender,
      isAmateur: quarantinePlayer.isAmateur ?? similarPlayer.isAmateur,
      isActive: quarantinePlayer.isActive ?? similarPlayer.isActive
    }),
    [quarantinePlayer, similarPlayer]
  )

  const confirmMerge = useCallback(() => {
    setLoading?.(true)
    golfEndpoints.quarantine
      .mergePlayer(quarantinePlayer.id, similarPlayer.id)
      .then((response) => {
        if (response.status === 200) {
          enqueueSnackbar(makeMergeSuccessMessage(quarantinePlayer, similarPlayer), {
            variant: 'success',
            autoHideDuration: 2000
          })
          onSubmit?.()
        } else {
          enqueueSnackbar(errorMsg, {
            variant: 'error',
            autoHideDuration: 2000
          })
        }
      })
      .catch(() => {
        enqueueSnackbar(errorMsg, {
          variant: 'error',
          autoHideDuration: 2000
        })
      })
      .finally(() => {
        setLoading?.(false)
      })
  }, [quarantinePlayer, similarPlayer, golfEndpoints, onSubmit])

  return (
    <ActionMergeQuarantineConfirm
      onClick={confirmMerge}
      mergeResultPlayer={mergeResultPlayer}
      displaySeparately={displaySeparately}
      confirmationMessage={
        overrideText || makeMergeConfirmationMessage(quarantinePlayer, similarPlayer)
      }
    />
  )
}
